import { QueryClient, keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";

import { getRanaApiV1 } from "@nens/rana/api-v1";
import { TenantContext } from "@nens/ranalib";
import { combineUrlAndParams } from "@nens/shared-utils";

import { queryDirectly } from "./hooks/query-directly";
import { getSearchStatePublicationId } from "./utils/get-search-state-publication-id";

interface Legend {
  name: string;
  colormap_type: "gradient" | "discrete";
  legend: { value: number; color: string }[];
  limits: [number, number];
  labels: Record<string, Record<string, string>>;
}

// Single (wms legend)

const getWmsLegendQuery = (
  tenantId: string,
  descriptorId: string,
  layer: string,
  style: string | undefined
) => {
  const publicationId = getSearchStatePublicationId();

  const baseUrl = publicationId
    ? `/tenants/${tenantId}/publications/${publicationId}/file-descriptors/${descriptorId}/wms`
    : `/tenants/${tenantId}/file-descriptors/${descriptorId}/wms`;

  const url = combineUrlAndParams(baseUrl, {
    service: "WMS",
    request: "GetLegend",
    layer,
    style,
  });

  const styleSplit = style?.split(":");
  return getRanaApiV1().getQueryOptions({
    queryKey: [
      "legend",
      "wms",
      descriptorId,
      layer,
      // [1] and [2] are for the min max of the style. We split them so we can get this query without the min max below
      ...(styleSplit ? [styleSplit[0], styleSplit[1], styleSplit[2]] : [null, null, null]),
    ],
    queryFn: async () => (await getRanaApiV1().BaseApi.fetchJson(url)) as Legend,
  });
};

export function useWmsLegendQuery(descriptorId: string, layer: string, style: string | undefined) {
  const { tenant } = useContext(TenantContext);
  return useQuery(getWmsLegendQuery(tenant, descriptorId, layer, style));
}

// Single (color-map-type)

function getColormapTypeQuery(
  queryClient: QueryClient,
  tenant: string,
  descriptorId: string | undefined,
  layer: string | undefined,
  style: string | undefined
) {
  const styleSplit = style?.split(":");
  return getRanaApiV1().getQueryOptions({
    queryKey: [
      "legend",
      "wms",
      "without-min-max",
      descriptorId,
      layer,
      ...(styleSplit ? [styleSplit[0]] : [null]),
    ],
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<Legend["colormap_type"]> => {
      if (!descriptorId || !layer) throw Error("Never happens");
      const wmsLegendQuery = getWmsLegendQuery(tenant, descriptorId, layer, style);
      const {
        data: wmsLegendNoMinMax,
        isError,
        error,
      } = await queryDirectly(
        queryClient,
        { ...wmsLegendQuery, queryKey: wmsLegendQuery.queryKey.slice(0, -2) },
        { exact: false }
      );

      if (isError) throw error;

      return wmsLegendNoMinMax.colormap_type;
    },
    enabled: !!descriptorId && !!layer,
  });
}

export function useColormapTypeQuery(
  descriptorId: string | undefined,
  layer: string | undefined,
  style: string | undefined
) {
  const { tenant } = useContext(TenantContext);
  const queryClient = useQueryClient();

  return useQuery(getColormapTypeQuery(queryClient, tenant, descriptorId, layer, style));
}
